import {
  AnalyticsEvent,
  ButtonComponentV2,
  Mutation,
  Typenames,
} from '@varo/graphql-schema-types';
import { useRouter } from 'expo-router';
import { useTabaPayAddress } from 'features/moveMoney/screens/dcf/tabapay/providers/TabaPayContextProvider';
import { getTabapayErrorScreen } from 'features/myVaro/screens/ExternalAccounts/helper/TabapayHelper';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UpdateExternalCardAddress } from 'services/graphql/mutations/UpdateExternalCardAddress';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import { useFormattedCombinedError } from 'shared/hooks/errorFormatters/useFormattedCombinedError/useFormattedCombinedError';
import { ErrorScreenType } from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import { isCombinedError } from 'shared/utils/errors/combinedErrorUtils';
import { isCoreError } from 'shared/utils/errors/CoreError';
import { useMutation } from 'urql';

const useUpdateExternalCardAddress = () => {
  const router = useRouter();
  const [result, executeMutation] = useMutation<Mutation>(
    UpdateExternalCardAddress,
  );
  const { t: translatedText } = useTranslation('myVaro', {
    keyPrefix: 'externalAccounts',
  });
  const {
    userCardDetailsAddress,
    setUpdatedMessage,
    setUserCardDetailsAddress,
  } = useTabaPayAddress();
  const addressText = userCardDetailsAddress;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorScreen, setErrorScreen] = useState<{
    screenType: ErrorScreenType.Error;
    messageHeadline: string;
    messageBody: string;
    primaryButton?: ButtonComponentV2;
    secondaryButton?: ButtonComponentV2;
    is3DSError?: boolean;
    analytics?: AnalyticsEvent;
  } | null>(null);
  const { defaultTitle, defaultMessage } = useDefaultErrorStrings();

  const responseError = useFormattedCombinedError(result.error);

  useEffect(() => {
    if (responseError) {
      setErrorScreen({
        screenType: ErrorScreenType.Error,
        messageHeadline: responseError.title,
        messageBody: responseError.message,
      });
    }
  }, [responseError, result.error]);

  const updateDebitCardAddress = async (externalCardId: string) => {
    try {
      setIsLoading(true);
      const result = await executeMutation({
        input: {
          externalCardId,
          address: addressText,
          isProfileAddress: false,
        },
      });
      const errorScreen = getTabapayErrorScreen(
        result?.data?.updateExternalCardAddress,
      );
      if (errorScreen) {
        setErrorScreen(errorScreen);
      }
      if (
        result?.data?.updateExternalCardAddress?.__typename ===
        Typenames.UpdateExternalCardAddressResultError
      ) {
        setErrorScreen({
          screenType: ErrorScreenType.Error,
          messageHeadline: result.data.updateExternalCardAddress.title,
          messageBody: result.data.updateExternalCardAddress.message,
        });
      }
      if (
        result?.data?.updateExternalCardAddress?.__typename ===
        Typenames.UpdateExternalCardAddressResultSuccess
      ) {
        const isSuccess = result.data.updateExternalCardAddress.success;
        if (isSuccess) {
          router.dismissTo({
            pathname: '/myvaro/external-accounts/[cardId]/card-details',
            params: { cardId: externalCardId },
          });
          setUpdatedMessage(translatedText('cardDetails.addressUpdated'));
        }
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      let errorTitle: string = defaultTitle;
      let errorMessage: string = defaultMessage;

      if (isCoreError(error)) {
        errorTitle = error.title;
        errorMessage = error.message;
      } else if (isCombinedError(error)) {
        errorMessage = error.message;
      }

      setErrorScreen({
        screenType: ErrorScreenType.Error,
        messageHeadline: errorTitle,
        messageBody: errorMessage,
      });
    }
  };
  return {
    updateDebitCardAddress,
    isLoading,
    errorScreen,
    addressText,
    setUserCardDetailsAddress,
  };
};

export default useUpdateExternalCardAddress;
